import { useEffect, useMemo, useState } from "react";
import { endOfDay, format, isBefore, startOfDay, startOfMonth, subDays } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { Button } from "@akuna/ui";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { PlanReportRange, PlanReportSummary } from "./plansModel";

interface PlansPeriodHeroProps {
  onRangeChange: (range: PlanReportRange) => void;
  range: PlanReportRange;
  summary: PlanReportSummary;
}

const PRESETS = ["today", "7d", "30d", "month"] as const;

const toIsoDate = (date: Date) => format(date, "yyyy-MM-dd");

function presetRange(preset: (typeof PRESETS)[number], now = new Date()): PlanReportRange {
  const to = endOfDay(now);
  const from = preset === "today"
    ? startOfDay(now)
    : preset === "7d"
      ? startOfDay(subDays(now, 6))
      : preset === "30d"
        ? startOfDay(subDays(now, 29))
        : startOfMonth(now);

  return { from: toIsoDate(from), to: toIsoDate(to) };
}

export function PlansPeriodHero({ onRangeChange, range, summary }: PlansPeriodHeroProps) {
  const { i18n, t } = useTranslation("daycareHotel");
  const locale = i18n.language.startsWith("pt") ? ptBR : enUS;
  const numberLocale = i18n.language.startsWith("pt") ? "pt-BR" : "en-US";
  const dateFrom = useMemo(() => startOfDay(new Date(`${range.from}T12:00:00`)), [range.from]);
  const dateTo = useMemo(() => endOfDay(new Date(`${range.to}T12:00:00`)), [range.to]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarRange, setCalendarRange] = useState<DateRange | undefined>({ from: dateFrom, to: dateTo });
  const [selectionStep, setSelectionStep] = useState<"from" | "to">("from");

  useEffect(() => {
    setCalendarRange({ from: dateFrom, to: dateTo });
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (calendarOpen) setSelectionStep("from");
  }, [calendarOpen]);

  const periodLabel = `${format(dateFrom, "dd MMM yyyy", { locale })} – ${format(dateTo, "dd MMM yyyy", { locale })}`;
  const endLabel = format(dateTo, "dd MMMM", { locale });
  const formatCurrency = (amount: number) => new Intl.NumberFormat(numberLocale, {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

  const isPresetActive = (preset: (typeof PRESETS)[number]) => {
    const next = presetRange(preset);
    return next.from === range.from && next.to === range.to;
  };

  function handleDayClick(day: Date) {
    const clicked = startOfDay(day);
    if (selectionStep === "from") {
      setCalendarRange({ from: clicked, to: undefined });
      setSelectionStep("to");
      return;
    }

    const selectedFrom = calendarRange?.from ? startOfDay(calendarRange.from) : clicked;
    const from = isBefore(clicked, selectedFrom) ? clicked : selectedFrom;
    const to = isBefore(clicked, selectedFrom) ? selectedFrom : clicked;
    setCalendarRange({ from, to });
    onRangeChange({ from: toIsoDate(from), to: toIsoDate(to) });
    setSelectionStep("from");
    setCalendarOpen(false);
  }

  return (
    <section className="flex flex-col gap-4 rounded-2xl border bg-card p-6 md:flex-row md:items-start md:justify-between">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
          <span className="text-xs font-semibold uppercase tracking-wider text-primary">{t("plans.report.operation")}</span>
        </div>
        <h2 className="text-2xl font-bold tracking-tight">{periodLabel}</h2>
        <p className="text-sm text-muted-foreground">{t("plans.report.periodContext", { date: endLabel })}</p>
        <p className="text-sm text-muted-foreground">
          {t("plans.report.heroSummary", {
            total: summary.total,
            overdue: summary.overdue,
            dueSoon: summary.dueSoon,
            lowBalance: summary.lowBalance,
            receivable: formatCurrency(summary.receivableAmount),
          })}
        </p>
      </div>

      <div className="flex shrink-0 flex-col gap-3">
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((preset) => {
            const key = preset === "today" ? "quickToday" : preset === "7d" ? "quick7Days" : preset === "30d" ? "quick30Days" : "quickMonth";
            return (
              <Button key={preset} className="h-7 rounded-full px-3 text-xs" onClick={() => onRangeChange(presetRange(preset))} size="sm" variant={isPresetActive(preset) ? "default" : "outline"}>
                {t(`plans.report.${key}`)}
              </Button>
            );
          })}
        </div>

        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <Button aria-label={t("plans.report.calendarLabel")} className="h-8 justify-start gap-2 text-xs" size="sm" variant="outline">
              <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="max-w-[220px] truncate">{periodLabel}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto p-0">
            <Calendar locale={locale} mode="range" numberOfMonths={2} onDayClick={handleDayClick} selected={calendarRange} />
          </PopoverContent>
        </Popover>
      </div>
    </section>
  );
}
